import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import {
    ChainAudienceRule,
    ChainDefinition,
    ChainOfferDocument,
    ChainOfferPreviewDto,
    ChainStep,
    CHAIN_HOME_SLOT_MODES,
    emptyChestStep,
    emptyFreeStep,
    emptyIapStep,
    emptyVideoStep,
    uniqueChainId,
} from '../models/dtos/ChainOfferDocument';
import {
    getChainOffers,
    previewChainOffer,
    rollbackChainOffers,
    updateChainOffers,
} from '../Api';

function tagsToText(tags: string[]): string {
    return tags.join(', ');
}

function textToTags(raw: string): string[] {
    return raw
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
}

function stepLabel(step: ChainStep, index: number): string {
    const base = `${index + 1}. ${step.type}`;
    if (step.type === 'iap') return `${base} · ${step.packName || step.storeCode || ''} ${step.priceLabel || ''}`.trim();
    return `${base} · ${step.coins.toLocaleString()} BC`;
}

export default function PromoChainOffersPage() {
    const [doc, setDoc] = useState<ChainOfferDocument | null>(null);
    const [persisted, setPersisted] = useState(false);
    const [canRollback, setCanRollback] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [selectedChainId, setSelectedChainId] = useState<string>('');
    const [previewId, setPreviewId] = useState('');
    const [preview, setPreview] = useState<ChainOfferPreviewDto | null>(null);

    const load = useCallback(async () => {
        setError(null);
        const bundle = await getChainOffers();
        if (!bundle) {
            setError('Could not load chain offers.');
            return;
        }
        setDoc(bundle.config);
        setPersisted(bundle.persisted);
        setCanRollback(bundle.canRollback);
        setWarnings(bundle.warnings || []);
        setSelectedChainId(prev => prev || bundle.config.chains[0]?.id || '');
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const selectedChain = useMemo(
        () => doc?.chains.find(c => c.id === selectedChainId) || null,
        [doc, selectedChainId],
    );

    const updateDoc = (mutator: (current: ChainOfferDocument) => ChainOfferDocument) => {
        setDoc(current => (current ? mutator({ ...current }) : current));
    };

    const updateChain = (chainId: string, mutator: (chain: ChainDefinition) => ChainDefinition) => {
        updateDoc(current => ({
            ...current,
            chains: current.chains.map(c => (c.id === chainId ? mutator({ ...c, steps: [...c.steps] }) : c)),
        }));
    };

    const updateAudience = (audienceId: string, mutator: (a: ChainAudienceRule) => ChainAudienceRule) => {
        updateDoc(current => ({
            ...current,
            audiences: current.audiences.map(a => (a.id === audienceId ? mutator({ ...a }) : a)),
        }));
    };

    const onSave = async () => {
        if (!doc) return;
        setSaving(true);
        setError(null);
        const result = await updateChainOffers(doc);
        setSaving(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setDoc(result.bundle.config);
        setPersisted(result.bundle.persisted);
        setCanRollback(result.bundle.canRollback);
        setWarnings(result.bundle.warnings || []);
    };

    const onRollback = async () => {
        setSaving(true);
        const result = await rollbackChainOffers();
        setSaving(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setDoc(result.bundle.config);
        setPersisted(result.bundle.persisted);
        setCanRollback(result.bundle.canRollback);
        setWarnings(result.bundle.warnings || []);
    };

    const onPreview = async () => {
        const id = Number(previewId);
        if (!Number.isFinite(id) || id <= 0) {
            setError('Enter a valid betupId');
            return;
        }
        const result = await previewChainOffer(id);
        setPreview(result);
        if (!result) setError('Preview failed');
    };

    if (!doc) {
        return (
            <Box p={3}>
                <Typography variant="h5">Chain Offers</Typography>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                <Typography sx={{ mt: 2 }}>Loading…</Typography>
            </Box>
        );
    }

    return (
        <Box p={3} maxWidth={1100}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h5">Chain Offers</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure reward chains, tag audiences, and Home placement. Client uses Design B (Sheet).
                        Version {doc.version}{persisted ? '' : ' · not persisted yet (defaults)'}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button disabled={!canRollback || saving} onClick={() => void onRollback()}>Rollback</Button>
                    <Button variant="contained" disabled={saving} onClick={() => void onSave()}>Save</Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {warnings.map(w => (
                <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
            ))}

            <Stack spacing={2} mb={3}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={doc.globalEnabled}
                            onChange={e => updateDoc(d => ({ ...d, globalEnabled: e.target.checked }))}
                        />
                    }
                    label="Global enabled"
                />
                <TextField
                    select
                    label="Home slot mode"
                    value={doc.homeSlotMode}
                    onChange={e => updateDoc(d => ({ ...d, homeSlotMode: e.target.value }))}
                    sx={{ maxWidth: 420 }}
                >
                    {CHAIN_HOME_SLOT_MODES.map(mode => (
                        <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Presentation"
                    value={doc.presentation}
                    onChange={e => updateDoc(d => ({ ...d, presentation: e.target.value }))}
                    sx={{ maxWidth: 280 }}
                >
                    <MenuItem value="SHEET">SHEET (Design B)</MenuItem>
                    <MenuItem value="DIALOG">DIALOG</MenuItem>
                    <MenuItem value="TRAIL">TRAIL</MenuItem>
                </TextField>
                <TextField
                    label="Timezone"
                    value={doc.timezone}
                    onChange={e => updateDoc(d => ({ ...d, timezone: e.target.value }))}
                    sx={{ maxWidth: 280 }}
                />
                <TextField
                    select
                    label="Default / leftover audience"
                    value={doc.defaultAudienceId}
                    onChange={e => updateDoc(d => ({ ...d, defaultAudienceId: e.target.value }))}
                    sx={{ maxWidth: 360 }}
                    helperText="Must have empty includeTags. Used when no segment matches or in holdout."
                >
                    {doc.audiences.map(a => (
                        <MenuItem key={a.id} value={a.id}>{a.id} → {a.chainId}</MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={
                        <Switch
                            checked={doc.experiment.enabled}
                            onChange={e => updateDoc(d => ({
                                ...d,
                                experiment: { ...d.experiment, enabled: e.target.checked },
                            }))}
                        />
                    }
                    label="Experiment holdout"
                />
                {doc.experiment.enabled && (
                    <Stack direction="row" spacing={2}>
                        <TextField
                            type="number"
                            label="Holdout %"
                            value={doc.experiment.holdoutPercent}
                            onChange={e => updateDoc(d => ({
                                ...d,
                                experiment: { ...d.experiment, holdoutPercent: Number(e.target.value) || 0 },
                            }))}
                            sx={{ width: 140 }}
                        />
                        <TextField
                            label="Salt"
                            value={doc.experiment.salt}
                            onChange={e => updateDoc(d => ({
                                ...d,
                                experiment: { ...d.experiment, salt: e.target.value },
                            }))}
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                )}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Audiences (cascade order)</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                First matching enabled audience with include tags wins (exclusive). Leftover is never in the cascade.
            </Typography>
            <Stack spacing={2} mb={3}>
                {doc.audiences.map((audience, index) => (
                    <Box key={audience.id} border={1} borderColor="divider" borderRadius={2} p={2}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <Chip size="small" label={`#${index + 1}`} />
                            <Typography fontWeight={600}>{audience.id}</Typography>
                            {audience.id === doc.defaultAudienceId && <Chip size="small" color="primary" label="leftover" />}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={audience.enabled}
                                        onChange={e => updateAudience(audience.id, a => ({ ...a, enabled: e.target.checked }))}
                                    />
                                }
                                label="Enabled"
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                label="Include tags"
                                fullWidth
                                value={tagsToText(audience.includeTags)}
                                onChange={e => updateAudience(audience.id, a => ({ ...a, includeTags: textToTags(e.target.value) }))}
                                disabled={audience.id === doc.defaultAudienceId}
                            />
                            <TextField
                                label="Exclude tags"
                                fullWidth
                                value={tagsToText(audience.excludeTags)}
                                onChange={e => updateAudience(audience.id, a => ({ ...a, excludeTags: textToTags(e.target.value) }))}
                            />
                            <TextField
                                select
                                label="Chain"
                                value={audience.chainId}
                                onChange={e => updateAudience(audience.id, a => ({ ...a, chainId: e.target.value }))}
                                sx={{ minWidth: 200 }}
                            >
                                {doc.chains.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.id}</MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </Box>
                ))}
                <Button
                    onClick={() => updateDoc(d => {
                        const id = uniqueChainId('audience', d.audiences.map(a => a.id));
                        return {
                            ...d,
                            audiences: [
                                ...d.audiences.filter(a => a.id !== d.defaultAudienceId),
                                {
                                    id,
                                    enabled: true,
                                    includeTags: ['New_Tag'],
                                    excludeTags: [],
                                    chainId: d.chains[0]?.id || 'videos_only',
                                },
                                ...d.audiences.filter(a => a.id === d.defaultAudienceId),
                            ],
                        };
                    })}
                >
                    Add audience
                </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Typography variant="h6">Chains</Typography>
                <TextField
                    select
                    size="small"
                    value={selectedChainId}
                    onChange={e => setSelectedChainId(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    {doc.chains.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.name} ({c.id})</MenuItem>
                    ))}
                </TextField>
                <Button
                    onClick={() => updateDoc(d => {
                        const id = uniqueChainId('chain', d.chains.map(c => c.id));
                        const chain: ChainDefinition = {
                            id,
                            name: 'New chain',
                            durationHours: 24,
                            onExpire: 'close_and_reassign',
                            reassignPolicy: 'next_calendar_day',
                            reassignCooldownHours: 24,
                            steps: [emptyVideoStep(), emptyFreeStep(), emptyChestStep()],
                        };
                        setSelectedChainId(id);
                        return { ...d, chains: [...d.chains, chain] };
                    })}
                >
                    Add chain
                </Button>
            </Stack>

            {selectedChain && (
                <Box border={1} borderColor="divider" borderRadius={2} p={2} mb={3}>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                label="Name"
                                value={selectedChain.name}
                                onChange={e => updateChain(selectedChain.id, c => ({ ...c, name: e.target.value }))}
                            />
                            <TextField
                                type="number"
                                label="Duration hours"
                                value={selectedChain.durationHours}
                                onChange={e => updateChain(selectedChain.id, c => ({
                                    ...c,
                                    durationHours: Number(e.target.value) || 1,
                                }))}
                                sx={{ width: 160 }}
                                helperText="Validity window (e.g. 24 = one day)"
                            />
                            <TextField
                                select
                                label="On expire"
                                value={selectedChain.onExpire}
                                onChange={e => updateChain(selectedChain.id, c => ({
                                    ...c,
                                    onExpire: e.target.value as ChainDefinition['onExpire'],
                                }))}
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="close_and_reassign">close_and_reassign</MenuItem>
                                <MenuItem value="reset_progress">reset_progress</MenuItem>
                                <MenuItem value="keep_claimed_freeze_ui">keep_claimed_freeze_ui</MenuItem>
                            </TextField>
                            <TextField
                                select
                                label="Reassign"
                                value={selectedChain.reassignPolicy}
                                onChange={e => updateChain(selectedChain.id, c => ({
                                    ...c,
                                    reassignPolicy: e.target.value as ChainDefinition['reassignPolicy'],
                                }))}
                                sx={{ minWidth: 200 }}
                                helperText="When the next chain can start after expire/complete"
                            >
                                <MenuItem value="next_calendar_day">next_calendar_day</MenuItem>
                                <MenuItem value="cooldown_hours">cooldown_hours</MenuItem>
                                <MenuItem value="after_duration">after_duration</MenuItem>
                            </TextField>
                            {selectedChain.reassignPolicy === 'cooldown_hours' && (
                                <TextField
                                    type="number"
                                    label="Reassign cooldown hours"
                                    value={selectedChain.reassignCooldownHours}
                                    onChange={e => updateChain(selectedChain.id, c => ({
                                        ...c,
                                        reassignCooldownHours: Number(e.target.value) || 1,
                                    }))}
                                    sx={{ width: 200 }}
                                    helperText="Wait this many hours after complete/expire"
                                />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Duration hours = how long the player has to finish this chain (countdown on Home/sheet).
                            Reassign = when a new chain can be assigned after it ends:
                            next_calendar_day (tomorrow in timezone), cooldown_hours (N hours later),
                            after_duration (as soon as duration elapsed).
                            Step Coins = what the celebrate dialog and balance credit use (not the Video Rewards placement amount).
                        </Typography>

                        <Typography fontWeight={600}>Steps (last must be chest / locker)</Typography>
                        {selectedChain.steps.map((step, index) => (
                            <Box key={`${selectedChain.id}-${index}`} bgcolor="action.hover" borderRadius={1} p={1.5}>
                                <Typography variant="body2" mb={1}>{stepLabel(step, index)}</Typography>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                    <TextField
                                        select
                                        size="small"
                                        label="Type"
                                        value={step.type}
                                        onChange={e => updateChain(selectedChain.id, c => {
                                            const steps = [...c.steps];
                                            const type = e.target.value as ChainStep['type'];
                                            steps[index] = type === 'video' ? emptyVideoStep()
                                                : type === 'free' ? emptyFreeStep()
                                                    : type === 'iap' ? emptyIapStep()
                                                        : emptyChestStep();
                                            return { ...c, steps };
                                        })}
                                        sx={{ width: 140 }}
                                    >
                                        <MenuItem value="video">video</MenuItem>
                                        <MenuItem value="free">free</MenuItem>
                                        <MenuItem value="iap">iap</MenuItem>
                                        <MenuItem value="chest">chest</MenuItem>
                                    </TextField>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Coins"
                                        value={step.coins}
                                        onChange={e => updateChain(selectedChain.id, c => {
                                            const steps = [...c.steps];
                                            const coins = Number(e.target.value) || 0;
                                            steps[index] = {
                                                ...steps[index],
                                                coins,
                                                grants: steps[index].grants.map(g =>
                                                    g.type === 'BETCOINS' ? { ...g, amount: coins } : g,
                                                ),
                                            };
                                            return { ...c, steps };
                                        })}
                                        sx={{ width: 140 }}
                                    />
                                    {step.type === 'video' && (
                                        <TextField
                                            size="small"
                                            label="Placement"
                                            value={step.placementId || ''}
                                            onChange={e => updateChain(selectedChain.id, c => {
                                                const steps = [...c.steps];
                                                steps[index] = { ...steps[index], placementId: e.target.value };
                                                return { ...c, steps };
                                            })}
                                        />
                                    )}
                                    {step.type === 'iap' && (
                                        <>
                                            <TextField
                                                size="small"
                                                label="Store code"
                                                value={step.storeCode || ''}
                                                onChange={e => updateChain(selectedChain.id, c => {
                                                    const steps = [...c.steps];
                                                    steps[index] = { ...steps[index], storeCode: e.target.value };
                                                    return { ...c, steps };
                                                })}
                                            />
                                            <TextField
                                                size="small"
                                                label="Pack name"
                                                value={step.packName || ''}
                                                onChange={e => updateChain(selectedChain.id, c => {
                                                    const steps = [...c.steps];
                                                    steps[index] = { ...steps[index], packName: e.target.value };
                                                    return { ...c, steps };
                                                })}
                                            />
                                            <TextField
                                                size="small"
                                                label="Price label"
                                                value={step.priceLabel || ''}
                                                onChange={e => updateChain(selectedChain.id, c => {
                                                    const steps = [...c.steps];
                                                    steps[index] = { ...steps[index], priceLabel: e.target.value };
                                                    return { ...c, steps };
                                                })}
                                            />
                                        </>
                                    )}
                                    {step.type === 'chest' && (
                                        <TextField
                                            size="small"
                                            label="Extra label"
                                            value={step.extraLabel || ''}
                                            onChange={e => updateChain(selectedChain.id, c => {
                                                const steps = [...c.steps];
                                                steps[index] = { ...steps[index], extraLabel: e.target.value };
                                                return { ...c, steps };
                                            })}
                                            sx={{ flex: 1 }}
                                        />
                                    )}
                                    <Button
                                        color="error"
                                        disabled={selectedChain.steps.length <= 2}
                                        onClick={() => updateChain(selectedChain.id, c => ({
                                            ...c,
                                            steps: c.steps.filter((_, i) => i !== index),
                                        }))}
                                    >
                                        Remove
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                        <Stack direction="row" spacing={1}>
                            <Button onClick={() => updateChain(selectedChain.id, c => ({
                                ...c,
                                steps: [...c.steps.slice(0, -1), emptyVideoStep(), c.steps[c.steps.length - 1]],
                            }))}>+ Video</Button>
                            <Button onClick={() => updateChain(selectedChain.id, c => ({
                                ...c,
                                steps: [...c.steps.slice(0, -1), emptyFreeStep(), c.steps[c.steps.length - 1]],
                            }))}>+ Free</Button>
                            <Button onClick={() => updateChain(selectedChain.id, c => ({
                                ...c,
                                steps: [...c.steps.slice(0, -1), emptyIapStep(), c.steps[c.steps.length - 1]],
                            }))}>+ IAP</Button>
                        </Stack>
                    </Stack>
                </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Preview user</Typography>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <TextField
                    label="betupId"
                    value={previewId}
                    onChange={e => setPreviewId(e.target.value)}
                    sx={{ width: 180 }}
                />
                <Button variant="outlined" onClick={() => void onPreview()}>Preview</Button>
            </Stack>
            {preview && (
                <Alert severity="info">
                    <div><strong>{preview.summary}</strong></div>
                    <div>found={String(preview.found)} tags=[{preview.tags.join(', ')}]</div>
                    <div>audience={preview.audienceId} chain={preview.chainId} group={preview.group}</div>
                    <div>step={preview.stepIndex} status={preview.status}</div>
                </Alert>
            )}
        </Box>
    );
}
